import * as THREE from 'three';
import { Howl } from 'howler';

export type ArenaThemeId = 'classic' | 'volcanic' | 'frozen' | 'observatory' | 'underwater';

export interface ArenaThemeDefinition {
  id: ArenaThemeId;
  name: string;
  description: string;
  // Materials
  floorColor: number;
  floorRoughness: number;
  floorMetalness: number;
  floorEmissive: number;
  floorEmissiveIntensity: number;
  pillarColor: number;
  pillarRoughness: number;
  pillarMetalness: number;
  pillarEmissive: number;
  pillarEmissiveIntensity: number;
  // Rune/ring colors
  runeColor: number;
  runeBaseOpacity: number;
  ringColor: number;
  // Sky and fog
  skyColor: number;
  fogColor: number;
  fogDensity: number;
  // Lighting
  ambientColor: number;
  ambientIntensity: number;
  mainLightColor: number;
  mainLightIntensity: number;
  fillLightColor: number;
  fillLightIntensity: number;
  rimLightColor: number;
  rimLightIntensity: number;
  hemiSkyColor: number;
  hemiGroundColor: number;
  hemiIntensity: number;
  // Torches
  torchColor: number;
  torchIntensity: number;
  torchHolderColor: number;
  // Flame particles
  flameColorR: [number, number]; // [base, range]
  flameColorG: [number, number];
  flameColorB: [number, number];
  flameSize: number;
  // Ambient particles
  ambientColorR: [number, number];
  ambientColorG: [number, number];
  ambientColorB: [number, number];
  ambientParticleSize: number;
  ambientParticleOpacity: number;
  // Animation overrides
  runeGlowSpeed: number;
  runeGlowRange: number;
  flameRiseSpeed: number;
  ambientDriftSpeed: number;
}

const CLASSIC_THEME: ArenaThemeDefinition = {
  id: 'classic',
  name: 'Classic Arena',
  description: 'The original stone arena with torches and golden runes.',
  floorColor: 0x3a3a3a,
  floorRoughness: 0.8,
  floorMetalness: 0.2,
  floorEmissive: 0x000000,
  floorEmissiveIntensity: 0,
  pillarColor: 0x4a4a4a,
  pillarRoughness: 0.9,
  pillarMetalness: 0.1,
  pillarEmissive: 0x000000,
  pillarEmissiveIntensity: 0,
  runeColor: 0xC9A227,
  runeBaseOpacity: 0.4,
  ringColor: 0xC9A227,
  skyColor: 0x0a0a15,
  fogColor: 0x2a2a3e,
  fogDensity: 0.015,
  ambientColor: 0x6666aa,
  ambientIntensity: 0.8,
  mainLightColor: 0xffffff,
  mainLightIntensity: 1.2,
  fillLightColor: 0x8899cc,
  fillLightIntensity: 0.6,
  rimLightColor: 0xffffee,
  rimLightIntensity: 0.4,
  hemiSkyColor: 0x8888ff,
  hemiGroundColor: 0x444422,
  hemiIntensity: 0.5,
  torchColor: 0xFF6B35,
  torchIntensity: 2,
  torchHolderColor: 0x3a3a3a,
  flameColorR: [1.0, 0],
  flameColorG: [0.3, 0.4],
  flameColorB: [0, 0],
  flameSize: 0.15,
  ambientColorR: [0.8, 0.2],
  ambientColorG: [0.6, 0.2],
  ambientColorB: [0.1, 0.2],
  ambientParticleSize: 0.05,
  ambientParticleOpacity: 0.6,
  runeGlowSpeed: 1.2,
  runeGlowRange: 0.25,
  flameRiseSpeed: 1.0,
  ambientDriftSpeed: 1.0,
};

const VOLCANIC_THEME: ArenaThemeDefinition = {
  id: 'volcanic',
  name: 'Volcanic Forge',
  description: 'A hellish arena of obsidian and lava, where the ground cracks with molten fire.',
  floorColor: 0x1a1010,
  floorRoughness: 0.6,
  floorMetalness: 0.3,
  floorEmissive: 0xff3300,
  floorEmissiveIntensity: 0.15,
  pillarColor: 0x1a1a1a,
  pillarRoughness: 0.7,
  pillarMetalness: 0.2,
  pillarEmissive: 0xff4400,
  pillarEmissiveIntensity: 0.12,
  runeColor: 0xff4400,
  runeBaseOpacity: 0.5,
  ringColor: 0xff3300,
  skyColor: 0x1a0500,
  fogColor: 0x3a1500,
  fogDensity: 0.02,
  ambientColor: 0xff6622,
  ambientIntensity: 0.6,
  mainLightColor: 0xff8844,
  mainLightIntensity: 1.0,
  fillLightColor: 0xff4400,
  fillLightIntensity: 0.4,
  rimLightColor: 0xff2200,
  rimLightIntensity: 0.5,
  hemiSkyColor: 0xff4422,
  hemiGroundColor: 0x331100,
  hemiIntensity: 0.4,
  torchColor: 0xff6600,
  torchIntensity: 2.5,
  torchHolderColor: 0x222222,
  flameColorR: [1.0, 0],
  flameColorG: [0.2, 0.3],
  flameColorB: [0.0, 0.05],
  flameSize: 0.2,
  ambientColorR: [1.0, 0],
  ambientColorG: [0.15, 0.2],
  ambientColorB: [0.0, 0.05],
  ambientParticleSize: 0.08,
  ambientParticleOpacity: 0.7,
  runeGlowSpeed: 0.8,
  runeGlowRange: 0.35,
  flameRiseSpeed: 1.3,
  ambientDriftSpeed: 0.8,
};

const FROZEN_THEME: ArenaThemeDefinition = {
  id: 'frozen',
  name: 'Frozen Tundra',
  description: 'An icy expanse of frost and snow, where pillars gleam with frozen light.',
  floorColor: 0xaabbcc,
  floorRoughness: 0.3,
  floorMetalness: 0.4,
  floorEmissive: 0x112244,
  floorEmissiveIntensity: 0.08,
  pillarColor: 0x88aacc,
  pillarRoughness: 0.2,
  pillarMetalness: 0.5,
  pillarEmissive: 0x2244aa,
  pillarEmissiveIntensity: 0.15,
  runeColor: 0x44aaff,
  runeBaseOpacity: 0.35,
  ringColor: 0x44aaff,
  skyColor: 0x0a1520,
  fogColor: 0x1a2a40,
  fogDensity: 0.018,
  ambientColor: 0x4488cc,
  ambientIntensity: 0.7,
  mainLightColor: 0xccddff,
  mainLightIntensity: 1.0,
  fillLightColor: 0x4466aa,
  fillLightIntensity: 0.5,
  rimLightColor: 0x88bbff,
  rimLightIntensity: 0.4,
  hemiSkyColor: 0x6688cc,
  hemiGroundColor: 0x223344,
  hemiIntensity: 0.5,
  torchColor: 0x44aaff,
  torchIntensity: 1.8,
  torchHolderColor: 0x667788,
  flameColorR: [0.3, 0.1],
  flameColorG: [0.6, 0.2],
  flameColorB: [1.0, 0],
  flameSize: 0.12,
  ambientColorR: [0.8, 0.2],
  ambientColorG: [0.85, 0.15],
  ambientColorB: [1.0, 0],
  ambientParticleSize: 0.06,
  ambientParticleOpacity: 0.5,
  runeGlowSpeed: 0.6,
  runeGlowRange: 0.2,
  flameRiseSpeed: 0.5,
  ambientDriftSpeed: 0.4,
};

const OBSERVATORY_THEME: ArenaThemeDefinition = {
  id: 'observatory',
  name: 'Starlit Observatory',
  description: 'A celestial arena beneath the stars, with marble floors and golden constellations.',
  floorColor: 0x1a1a2e,
  floorRoughness: 0.4,
  floorMetalness: 0.3,
  floorEmissive: 0x111122,
  floorEmissiveIntensity: 0.05,
  pillarColor: 0x888899,
  pillarRoughness: 0.3,
  pillarMetalness: 0.2,
  pillarEmissive: 0x222233,
  pillarEmissiveIntensity: 0.05,
  runeColor: 0xffcc44,
  runeBaseOpacity: 0.45,
  ringColor: 0xffcc44,
  skyColor: 0x050510,
  fogColor: 0x080818,
  fogDensity: 0.008,
  ambientColor: 0x334466,
  ambientIntensity: 0.5,
  mainLightColor: 0xccccff,
  mainLightIntensity: 0.8,
  fillLightColor: 0x444466,
  fillLightIntensity: 0.3,
  rimLightColor: 0xffddaa,
  rimLightIntensity: 0.3,
  hemiSkyColor: 0x444488,
  hemiGroundColor: 0x222211,
  hemiIntensity: 0.4,
  torchColor: 0xffeebb,
  torchIntensity: 1.5,
  torchHolderColor: 0x556677,
  flameColorR: [1.0, 0],
  flameColorG: [0.9, 0.1],
  flameColorB: [0.6, 0.2],
  flameSize: 0.1,
  ambientColorR: [1.0, 0],
  ambientColorG: [0.95, 0.05],
  ambientColorB: [0.7, 0.3],
  ambientParticleSize: 0.04,
  ambientParticleOpacity: 0.7,
  runeGlowSpeed: 0.5,
  runeGlowRange: 0.2,
  flameRiseSpeed: 0.3,
  ambientDriftSpeed: 0.3,
};

const UNDERWATER_THEME: ArenaThemeDefinition = {
  id: 'underwater',
  name: 'Underwater Ruins',
  description: 'Sunken temple ruins where bioluminescence lights the depths.',
  floorColor: 0x2a4a3a,
  floorRoughness: 0.7,
  floorMetalness: 0.15,
  floorEmissive: 0x003322,
  floorEmissiveIntensity: 0.06,
  pillarColor: 0x3a5a4a,
  pillarRoughness: 0.8,
  pillarMetalness: 0.15,
  pillarEmissive: 0x004433,
  pillarEmissiveIntensity: 0.08,
  runeColor: 0x22ffaa,
  runeBaseOpacity: 0.4,
  ringColor: 0x22ffaa,
  skyColor: 0x001015,
  fogColor: 0x003344,
  fogDensity: 0.025,
  ambientColor: 0x226655,
  ambientIntensity: 0.6,
  mainLightColor: 0x44ccaa,
  mainLightIntensity: 0.8,
  fillLightColor: 0x226644,
  fillLightIntensity: 0.4,
  rimLightColor: 0x44ffcc,
  rimLightIntensity: 0.3,
  hemiSkyColor: 0x2288aa,
  hemiGroundColor: 0x112222,
  hemiIntensity: 0.4,
  torchColor: 0x22ffaa,
  torchIntensity: 1.6,
  torchHolderColor: 0x3a4a4a,
  flameColorR: [0.1, 0.1],
  flameColorG: [0.8, 0.2],
  flameColorB: [0.6, 0.2],
  flameSize: 0.13,
  ambientColorR: [0.1, 0.15],
  ambientColorG: [0.5, 0.3],
  ambientColorB: [0.8, 0.2],
  ambientParticleSize: 0.07,
  ambientParticleOpacity: 0.5,
  runeGlowSpeed: 0.7,
  runeGlowRange: 0.3,
  flameRiseSpeed: 0.6,
  ambientDriftSpeed: 0.5,
};

export const ARENA_THEMES: Record<ArenaThemeId, ArenaThemeDefinition> = {
  classic: CLASSIC_THEME,
  volcanic: VOLCANIC_THEME,
  frozen: FROZEN_THEME,
  observatory: OBSERVATORY_THEME,
  underwater: UNDERWATER_THEME,
};

const STORAGE_KEY = 'wizard_duel_arena_theme';

/**
 * Manages arena theme selection and application.
 * Swaps materials, lighting, fog, particles, and ambient audio
 * on the existing Arena geometry without rebuilding the scene.
 */
export class ArenaThemeManager {
  private currentTheme: ArenaThemeId;
  private ambientAudio: Howl | null = null;
  private audioContext: AudioContext | null = null;
  private blobUrl: string | null = null;

  constructor() {
    this.currentTheme = this.loadTheme();
  }

  private loadTheme(): ArenaThemeId {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && saved in ARENA_THEMES) {
        return saved as ArenaThemeId;
      }
    } catch { /* ignore */ }
    return 'classic';
  }

  saveTheme(themeId: ArenaThemeId): void {
    this.currentTheme = themeId;
    try {
      localStorage.setItem(STORAGE_KEY, themeId);
    } catch { /* ignore */ }
  }

  getCurrentThemeId(): ArenaThemeId {
    return this.currentTheme;
  }

  getTheme(id?: ArenaThemeId): ArenaThemeDefinition {
    return ARENA_THEMES[id ?? this.currentTheme];
  }

  getAllThemes(): ArenaThemeDefinition[] {
    return Object.values(ARENA_THEMES);
  }

  /**
   * Apply the current theme to the arena's scene objects.
   * Uses shared materials for InstancedMesh objects instead of iterating arrays.
   */
  applyTheme(
    floor: THREE.Mesh,
    pillarMaterial: THREE.MeshStandardMaterial,
    torches: THREE.PointLight[],
    torchHolderMaterial: THREE.MeshStandardMaterial,
    flameParticles: THREE.Points[],
    rings: THREE.Mesh[],
    runeMaterial: THREE.MeshBasicMaterial,
    lights: THREE.Light[],
    skybox: THREE.Mesh,
    fog: THREE.FogExp2,
    ambientParticles: THREE.Points
  ): void {
    const theme = this.getTheme();

    // Floor material
    const floorMat = floor.material as THREE.MeshStandardMaterial;
    floorMat.color.setHex(theme.floorColor);
    floorMat.roughness = theme.floorRoughness;
    floorMat.metalness = theme.floorMetalness;
    floorMat.emissive.setHex(theme.floorEmissive);
    floorMat.emissiveIntensity = theme.floorEmissiveIntensity;
    floorMat.needsUpdate = true;

    // Pillar shared material (updates all instanced pillars at once)
    pillarMaterial.color.setHex(theme.pillarColor);
    pillarMaterial.roughness = theme.pillarRoughness;
    pillarMaterial.metalness = theme.pillarMetalness;
    pillarMaterial.emissive.setHex(theme.pillarEmissive);
    pillarMaterial.emissiveIntensity = theme.pillarEmissiveIntensity;
    pillarMaterial.needsUpdate = true;

    // Rune shared material (updates all instanced runes at once)
    runeMaterial.color.setHex(theme.runeColor);
    runeMaterial.opacity = theme.runeBaseOpacity;
    runeMaterial.needsUpdate = true;

    // Ring colors
    rings.forEach(ring => {
      const mat = ring.material as THREE.MeshBasicMaterial;
      mat.color.setHex(theme.ringColor);
      mat.needsUpdate = true;
    });

    // Skybox
    const skyboxMat = skybox.material as THREE.MeshBasicMaterial;
    skyboxMat.color.setHex(theme.skyColor);
    skyboxMat.needsUpdate = true;

    // Fog
    fog.color.setHex(theme.fogColor);
    fog.density = theme.fogDensity;

    // Torches
    torches.forEach(torch => {
      torch.color.setHex(theme.torchColor);
      torch.intensity = theme.torchIntensity;
    });

    // Torch holder shared material
    torchHolderMaterial.color.setHex(theme.torchHolderColor);
    torchHolderMaterial.needsUpdate = true;

    // Flame particle colors
    flameParticles.forEach(flame => {
      const colors = flame.geometry.attributes.color.array as Float32Array;
      for (let i = 0; i < colors.length / 3; i++) {
        colors[i * 3] = theme.flameColorR[0] + Math.random() * theme.flameColorR[1];
        colors[i * 3 + 1] = theme.flameColorG[0] + Math.random() * theme.flameColorG[1];
        colors[i * 3 + 2] = theme.flameColorB[0] + Math.random() * theme.flameColorB[1];
      }
      flame.geometry.attributes.color.needsUpdate = true;
      const mat = flame.material as THREE.PointsMaterial;
      mat.size = theme.flameSize;
      mat.needsUpdate = true;
    });

    // Ambient particle colors
    if (ambientParticles) {
      const colors = ambientParticles.geometry.attributes.color.array as Float32Array;
      for (let i = 0; i < colors.length / 3; i++) {
        colors[i * 3] = theme.ambientColorR[0] + Math.random() * theme.ambientColorR[1];
        colors[i * 3 + 1] = theme.ambientColorG[0] + Math.random() * theme.ambientColorG[1];
        colors[i * 3 + 2] = theme.ambientColorB[0] + Math.random() * theme.ambientColorB[1];
      }
      ambientParticles.geometry.attributes.color.needsUpdate = true;
      const mat = ambientParticles.material as THREE.PointsMaterial;
      mat.size = theme.ambientParticleSize;
      mat.opacity = theme.ambientParticleOpacity;
      mat.needsUpdate = true;
    }

    // Lighting (lights array: [ambient, mainDir, fillDir, rimDir, hemisphere])
    if (lights.length >= 5) {
      const ambient = lights[0] as THREE.AmbientLight;
      ambient.color.setHex(theme.ambientColor);
      ambient.intensity = theme.ambientIntensity;

      const mainDir = lights[1] as THREE.DirectionalLight;
      mainDir.color.setHex(theme.mainLightColor);
      mainDir.intensity = theme.mainLightIntensity;

      const fillDir = lights[2] as THREE.DirectionalLight;
      fillDir.color.setHex(theme.fillLightColor);
      fillDir.intensity = theme.fillLightIntensity;

      const rimDir = lights[3] as THREE.DirectionalLight;
      rimDir.color.setHex(theme.rimLightColor);
      rimDir.intensity = theme.rimLightIntensity;

      const hemi = lights[4] as THREE.HemisphereLight;
      hemi.color.setHex(theme.hemiSkyColor);
      hemi.groundColor.setHex(theme.hemiGroundColor);
      hemi.intensity = theme.hemiIntensity;
    }
  }

  /**
   * Create and start playing theme-specific ambient audio.
   * Uses procedural synthesis -- no external files.
   */
  startAmbientAudio(musicVolume: number): void {
    this.stopAmbientAudio();

    this.audioContext = new (
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();

    const ctx = this.audioContext;
    const theme = this.currentTheme;

    // Only generate custom ambient for non-classic themes
    // Classic uses the existing arena ambience in AudioManager
    if (theme === 'classic') return;

    const duration = 20;
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    switch (theme) {
      case 'volcanic':
        this.generateVolcanicAmbient(left, right, numSamples, sampleRate);
        break;
      case 'frozen':
        this.generateFrozenAmbient(left, right, numSamples, sampleRate);
        break;
      case 'observatory':
        this.generateObservatoryAmbient(left, right, numSamples, sampleRate);
        break;
      case 'underwater':
        this.generateUnderwaterAmbient(left, right, numSamples, sampleRate);
        break;
    }

    // Normalize
    let maxVal = 0;
    for (let i = 0; i < numSamples; i++) {
      maxVal = Math.max(maxVal, Math.abs(left[i]), Math.abs(right[i]));
    }
    if (maxVal > 0.9) {
      const normalize = 0.85 / maxVal;
      for (let i = 0; i < numSamples; i++) {
        left[i] *= normalize;
        right[i] *= normalize;
      }
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    this.blobUrl = URL.createObjectURL(blob);

    this.ambientAudio = new Howl({
      src: [this.blobUrl],
      volume: musicVolume * 0.4,
      loop: true,
      format: ['wav'],
    });
    this.ambientAudio.play();
  }

  stopAmbientAudio(): void {
    if (this.ambientAudio) {
      this.ambientAudio.stop();
      this.ambientAudio.unload();
      this.ambientAudio = null;
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  setAmbientVolume(volume: number): void {
    if (this.ambientAudio) {
      this.ambientAudio.volume(volume * 0.4);
    }
  }

  // ----- Procedural ambient audio generators -----

  private generateVolcanicAmbient(
    left: Float32Array,
    right: Float32Array,
    numSamples: number,
    sampleRate: number
  ): void {
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      let valueL = 0;
      let valueR = 0;

      // Deep rumbling base
      const rumbleSwell = 0.6 + Math.sin(2 * Math.PI * 0.07 * t) * 0.4;
      const rumble = Math.sin(2 * Math.PI * 25 * t) * 0.08 * rumbleSwell +
                     Math.sin(2 * Math.PI * 40 * t) * 0.05 * rumbleSwell;
      valueL += rumble;
      valueR += rumble;

      // Bubbling lava (irregular pops)
      const bubbleRate = 8 + Math.sin(t * 2.3) * 4;
      const bubblePhase = (t * bubbleRate) % 1;
      if (bubblePhase < 0.03) {
        const pop = Math.exp(-bubblePhase * 100) * (Math.random() - 0.3) * 0.12;
        const pan = Math.sin(t * 1.7) * 0.6;
        valueL += pop * (1 + pan);
        valueR += pop * (1 - pan);
      }

      // Fire crackling
      const crackleRate = 15 + Math.sin(t * 1.5) * 8;
      const cracklePhase = (t * crackleRate) % 1;
      if (cracklePhase < 0.04) {
        const crackle = Math.exp(-cracklePhase * 60) * (Math.random() - 0.5) * 0.1;
        valueL += crackle * 1.3;
        valueR += crackle * 0.7;
      }

      // Distant eruption rumbles (very low frequency)
      const eruptionSwell = Math.sin(2 * Math.PI * 0.04 * t);
      if (eruptionSwell > 0.7) {
        const eruptionIntensity = (eruptionSwell - 0.7) * 3.33;
        const eruption = Math.sin(2 * Math.PI * 18 * t) * 0.04 * eruptionIntensity +
                        (Math.random() - 0.5) * 0.02 * eruptionIntensity;
        valueL += eruption;
        valueR += eruption;
      }

      // Low fire hiss
      const hiss = (Math.random() - 0.5) * 0.025;
      valueL += hiss;
      valueR += hiss * 0.8;

      left[i] = valueL;
      right[i] = valueR;
    }
  }

  private generateFrozenAmbient(
    left: Float32Array,
    right: Float32Array,
    numSamples: number,
    sampleRate: number
  ): void {
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      let valueL = 0;
      let valueR = 0;

      // Howling wind (filtered noise with sweeping frequency)
      const windSwell = Math.sin(2 * Math.PI * 0.06 * t) * 0.5 + 0.5;
      const windBase = (Math.random() - 0.5) * 0.04 * windSwell;
      const windPan = Math.sin(2 * Math.PI * 0.09 * t);
      valueL += windBase * (1 + windPan);
      valueR += windBase * (1 - windPan);

      // Wind whistle (higher frequency)
      const whistleFreq = 500 + Math.sin(2 * Math.PI * 0.08 * t) * 250;
      const whistleSwell = Math.pow(Math.max(0, Math.sin(2 * Math.PI * 0.05 * t)), 3);
      const whistle = Math.sin(2 * Math.PI * whistleFreq * t) * 0.008 * whistleSwell;
      valueL += whistle * 0.8;
      valueR += whistle * 1.2;

      // Ice cracking (occasional sharp transients)
      const crackTrigger = Math.sin(t * 3.7 + Math.sin(t * 0.3) * 5);
      if (crackTrigger > 0.97) {
        const crackT = (crackTrigger - 0.97) * 33.33;
        const crack = Math.exp(-crackT * 30) * (Math.random() - 0.5) * 0.08;
        const crackPan = Math.sin(t * 7.1);
        valueL += crack * (1 + crackPan);
        valueR += crack * (1 - crackPan);
      }

      // Crystalline chimes (rare high tones)
      const chimeTrigger = Math.sin(t * 0.43 + 2.1);
      if (chimeTrigger > 0.95) {
        const chimeT = (chimeTrigger - 0.95) * 20;
        const chimeFreq = 2000 + Math.sin(t * 0.7) * 500;
        const chime = Math.sin(2 * Math.PI * chimeFreq * t) * 0.015 * Math.exp(-chimeT * 4);
        valueL += chime * 0.6;
        valueR += chime * 1.4;
      }

      // Muffled low ambience (silence feeling)
      const muffle = Math.sin(2 * Math.PI * 60 * t) * 0.01;
      valueL += muffle;
      valueR += muffle;

      left[i] = valueL;
      right[i] = valueR;
    }
  }

  private generateObservatoryAmbient(
    left: Float32Array,
    right: Float32Array,
    numSamples: number,
    sampleRate: number
  ): void {
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      let valueL = 0;
      let valueR = 0;

      // Celestial pad (deep, evolving drone with harmonics)
      const padFreq = 55;
      const padSwell = 0.5 + Math.sin(2 * Math.PI * 0.03 * t) * 0.3 +
                        Math.sin(2 * Math.PI * 0.07 * t) * 0.2;
      const pad = Math.sin(2 * Math.PI * padFreq * t) * 0.06 * padSwell +
                  Math.sin(2 * Math.PI * padFreq * 1.5 * t) * 0.04 * padSwell +
                  Math.sin(2 * Math.PI * padFreq * 2 * t) * 0.03 * padSwell;
      valueL += pad;
      valueR += pad * (0.95 + Math.sin(2 * Math.PI * 0.02 * t) * 0.05);

      // Cosmic hum (very low, pulsing)
      const cosmicHum = Math.sin(2 * Math.PI * 30 * t) * 0.02 *
                        (0.7 + Math.sin(2 * Math.PI * 0.1 * t) * 0.3);
      valueL += cosmicHum;
      valueR += cosmicHum;

      // Gentle wind (very subtle)
      const gentleWind = (Math.random() - 0.5) * 0.01 *
                         (0.5 + Math.sin(2 * Math.PI * 0.04 * t) * 0.5);
      valueL += gentleWind;
      valueR += gentleWind;

      // Soft chimes (melodic bell tones)
      const chimeNotes = [523.25, 659.25, 783.99, 1046.5]; // C E G C octave
      const chimeIndex = Math.floor((t * 0.3) % chimeNotes.length);
      const chimePhase = (t * 0.3) % 1;
      if (chimePhase < 0.1) {
        const chimeEnv = Math.exp(-chimePhase * 8);
        const chimeFreq = chimeNotes[chimeIndex];
        const chime = Math.sin(2 * Math.PI * chimeFreq * t) * 0.012 * chimeEnv +
                      Math.sin(2 * Math.PI * chimeFreq * 2 * t) * 0.005 * chimeEnv;
        const chimePan = Math.sin(t * 0.5 + chimeIndex);
        valueL += chime * (1 + chimePan * 0.4);
        valueR += chime * (1 - chimePan * 0.4);
      }

      // Distant star twinkle (tiny random high sparkles)
      if (Math.random() > 0.998) {
        const sparkle = Math.sin(2 * Math.PI * (3000 + Math.random() * 2000) * t) * 0.008;
        const sp = Math.random() - 0.5;
        valueL += sparkle * (1 + sp);
        valueR += sparkle * (1 - sp);
      }

      left[i] = valueL;
      right[i] = valueR;
    }
  }

  private generateUnderwaterAmbient(
    left: Float32Array,
    right: Float32Array,
    numSamples: number,
    sampleRate: number
  ): void {
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      let valueL = 0;
      let valueR = 0;

      // Muffled underwater ambience (low-pass filtered noise)
      const underwaterNoise = (Math.random() - 0.5) * 0.02;
      const underFilter = 0.6 + Math.sin(2 * Math.PI * 0.05 * t) * 0.3;
      valueL += underwaterNoise * underFilter;
      valueR += underwaterNoise * underFilter * 0.9;

      // Gentle current flow (very low rumble)
      const currentSwell = 0.5 + Math.sin(2 * Math.PI * 0.04 * t) * 0.5;
      const current = Math.sin(2 * Math.PI * 35 * t) * 0.04 * currentSwell +
                     Math.sin(2 * Math.PI * 50 * t) * 0.02 * currentSwell;
      const currentPan = Math.sin(2 * Math.PI * 0.06 * t);
      valueL += current * (1 + currentPan * 0.3);
      valueR += current * (1 - currentPan * 0.3);

      // Bubble streams (irregular rising pops)
      const bubbleRate = 6 + Math.sin(t * 1.3) * 3;
      const bubblePhase = (t * bubbleRate) % 1;
      if (bubblePhase < 0.02) {
        const bubbleFreq = 800 + Math.random() * 400;
        const pop = Math.sin(2 * Math.PI * bubbleFreq * t) * 0.015 *
                    Math.exp(-bubblePhase * 80);
        const bpan = (Math.random() - 0.5) * 1.4;
        valueL += pop * (1 + bpan);
        valueR += pop * (1 - bpan);
      }

      // Whale song (distant, slow frequency sweeps)
      const whaleSwell = Math.pow(Math.max(0, Math.sin(2 * Math.PI * 0.03 * t)), 4);
      if (whaleSwell > 0.1) {
        const whaleFreq = 80 + Math.sin(2 * Math.PI * 0.2 * t) * 30;
        const whale = Math.sin(2 * Math.PI * whaleFreq * t) * 0.02 * whaleSwell;
        valueL += whale * 0.7;
        valueR += whale * 1.3;
      }

      // Bioluminescent sparkle (tiny high pings)
      if (Math.random() > 0.997) {
        const bioFreq = 1500 + Math.random() * 1000;
        const bio = Math.sin(2 * Math.PI * bioFreq * t) * 0.008;
        const bp = Math.random() - 0.5;
        valueL += bio * (1 + bp);
        valueR += bio * (1 - bp);
      }

      left[i] = valueL;
      right[i] = valueR;
    }
  }

  // --- WAV encoder (same pattern as AudioManager) ---

  private bufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1;
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const samples = buffer.length;
    const dataSize = samples * blockAlign;
    const bufferSize = 44 + dataSize;

    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);

    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    const channels: Float32Array[] = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < samples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }

    return arrayBuffer;
  }

  dispose(): void {
    this.stopAmbientAudio();
  }
}
